import React, { useState, useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import ReusableTable from "../../dashboard/ReusableTable";
import ReusableForm from "../../common/ReusableForm";
import ReusableDeleteDialog from "../../common/ReusableDeleteDialog";
import { getRequest, postRequest } from "../../services/apiService"; // Removed unused request methods

export default function ActivityMaster() {
  const [activities, setActivities] = useState([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    desc: "",
    status_db: 1,
  });
  const toast = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const formFields = [
    { name: "name", label: "Activity Name", type: "text", required: true },
    { name: "desc", label: "Description", type: "text" },
  ];

  // Delete form fields
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  const [deleteFormData, setDeleteFormData] = useState({
    confirmation: ""
  });

  // Fetch activities function
  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await getRequest(`master/activities/?search=${encodeURIComponent(search)}&page=${currentPage}`);
      setActivities(Array.isArray(response.results?.data) ? response.results.data : []);
      setTotalRecords(response.count || 0);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch activities",
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  // Update useEffect to use fetchActivities
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      fetchActivities();
    }, 700);
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search, currentPage]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  const saveActivity = async () => {
    if (!formData.name) {
      toast.current.show({
        severity: "error",
        summary: "Validation",
        detail: "Activity name is required",
        life: 3000
      });
      return;
    }

    try {
      const payload = {
        name: formData.name,
        desc: formData.desc,
        status_db: formData.status_db
      };

      // Use POST for both create and update
      if (isEditing && formData.id) {
        // Add ID to payload for updates
        payload.id = formData.id;
        await postRequest("master/activities/", payload);
        toast.current.show({ severity: "success", summary: "Updated", detail: "Activity updated", life: 3000 });
      } else {
        await postRequest("master/activities/", payload);
        toast.current.show({ severity: "success", summary: "Added", detail: "Activity added", life: 3000 });
        setCurrentPage(1);
      }

      await fetchActivities(); // Refresh table after save
      setDialogVisible(false);
    } catch (error) {
      console.error("Error saving activity:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save activity",
        life: 3000
      });
    }
  };

  const openAddDialog = () => {
    setFormData({ id: null, name: "", desc: "", status_db: 1 });
    setIsEditing(false);
    setDialogVisible(true);
  };

  const openEditDialog = (activity) => {
    setFormData({ ...activity });
    setIsEditing(true);
    setDialogVisible(true);
  };

  const openDeleteModal = (activity) => {
    setActivityToDelete(activity);
    setShowDeleteModal(true);
  };

  const deleteActivity = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please type 'DELETE' to confirm",
        life: 3000
      });
      return;
    }

    try {
      // Use POST with status_db=3 and ID in payload for deletion
      await postRequest("master/activities/", {
        id: activityToDelete.id,
        status_db: 3
      });
      
      toast.current.show({
        severity: "success",
        summary: "Deleted",
        detail: "Activity deleted successfully",
        life: 3000
      });
      await fetchActivities(); // Refresh table after delete
      setShowDeleteModal(false);
      setActivityToDelete(null);
      setDeleteFormData({ confirmation: "" });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete activity",
        life: 3000
      });
    }
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const columns = [
    { field: "name", header: "Activity Name", sortable: true },
    { field: "desc", header: "Description", sortable: true },
  ];

  const actionItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: (rowData) => openEditDialog(rowData)
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: (rowData) => openDeleteModal(rowData)
    }
  ];

  return (
    <div className="min-h-screen bg-navy-900 text-white">
      <Toast ref={toast} position="top-right" />

      <ReusableTable
        title="Activity Details"
        data={activities}
        setData={setActivities}
        columnFields={columns}
        setAddNewDialogVisible={openAddDialog}
        actionMenu={true}
        actionItems={actionItems}
        loading={loading}
        totalRecords={totalRecords}
        rows={rowsPerPage}
        onPageChange={onPageChange}
        first={(currentPage - 1) * rowsPerPage}
        searchValue={search}
        onSearchChange={handleTableSearch}
      />

      {/* Add/Edit Modal with explicit wrapper */}
      {dialogVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full mx-auto max-w-lg animate-fade-in text-black">
            <ReusableForm
              headerText={isEditing ? "Edit Activity" : "Add New Activity"}
              formData={formData}
              setFormData={setFormData}
              fields={formFields}
              onSubmit={saveActivity}
              onHide={() => setDialogVisible(false)}
              loading={loading} // Pass loading state if ReusableForm handles it
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ReusableDeleteDialog
        visible={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={deleteActivity}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete Activity"
        description={`Are you sure you want to delete activity "${activityToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
        deleteLabel="Delete"
      />
    </div>
  );
}